export type ChangeListener = () => void;

/**
 * Base class for all ViewModels.
 * Provides simple event pub/sub for notifying React components of state changes.
 *
 * ViewModels extend this class and call `notifyChange()` after modifying state
 * to trigger component re-renders.
 */
export abstract class BaseViewModel {
    private listeners: Set<ChangeListener> = new Set();

    /**
     * Subscribe to changes in this ViewModel.
     * Called by useViewModel hook to trigger component re-renders.
     */
    subscribe(listener: ChangeListener): () => void {
        this.listeners.add(listener);
        return () => {
            this.listeners.delete(listener);
        };
    }

    /**
     * Notify all subscribers that state has changed.
     * ViewModels should call this after modifying any state that affects the view.
     */
    protected notifyChange(): void {
        this.listeners.forEach((listener) => listener());
    }

    /**
     * Override for cleanup logic. Called when component unmounts.
     */
    dispose(): void {
        this.listeners.clear();
    }
}
